import { useEffect, useMemo, useState } from "react";
import { getTicket } from "../api";
import { callPurposeInstruction } from "../callPurpose";
import { Layout } from "../components/Layout";
import { QrImage } from "../components/QrImage";
import { StatusBadge } from "../components/StatusBadge";
import type { QueueTicket } from "../types";

export function TicketPage() {
  const token = window.location.pathname.match(/^\/ticket\/([^/]+)\/?$/)?.[1] ?? "";
  const [ticket, setTicket] = useState<QueueTicket | null>(null);
  const [error, setError] = useState("");

  const ticketUrl = useMemo(() => window.location.href, []);

  useEffect(() => {
    let active = true;
    let timer: number | undefined;

    async function refresh() {
      try {
        const next = await getTicket(token);
        if (!active) return;
        setTicket(next);
        setError("");
        if (next.status === "WAITING" || next.status === "CALLED") {
          timer = window.setTimeout(refresh, 10_000);
        }
      } catch (caught) {
        if (!active) return;
        setError(caught instanceof Error ? caught.message : "ไม่พบบัตรคิว");
      }
    }

    void refresh();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [token]);

  if (error) {
    return (
      <Layout compact>
        <section className="empty-state">
          <span>!</span>
          <h1>เปิดบัตรคิวไม่ได้</h1>
          <p>{error}</p>
          <a className="secondary-button" href="/">กลับหน้าจองคิว</a>
        </section>
      </Layout>
    );
  }

  if (!ticket) {
    return (
      <Layout compact>
        <div className="loading-block page-loading">
          <span className="spinner" />
          <strong>กำลังโหลดบัตรคิว…</strong>
        </div>
      </Layout>
    );
  }

  const isCalled = ticket.status === "CALLED";
  const callInstruction = callPurposeInstruction(ticket.callPurpose);
  return (
    <Layout compact>
      <section className={`ticket-card ${isCalled ? "ticket-card--called" : ""}`}>
        <div className="ticket-head">
          <span>บัตรคิวของคุณ</span>
          <StatusBadge status={ticket.status} />
        </div>
        <div className="queue-number">
          <small>QUEUE NUMBER</small>
          <strong>{ticket.queueNumber}</strong>
        </div>

        {isCalled ? (
          <div className="call-banner">
            <b>ถึงคิวของคุณแล้ว</b>
            <span>{callInstruction ?? "กรุณาติดต่อเจ้าหน้าที่"}</span>
          </div>
        ) : ticket.status === "WAITING" ? (
          <div className="ahead">
            <span>คิวก่อนหน้าคุณ</span>
            <strong>{ticket.aheadCount}</strong>
            <span>คิว</span>
          </div>
        ) : (
          <div className="call-banner call-banner--muted">
            <b>รายการคิวนี้สิ้นสุดแล้ว</b>
            <span>สามารถกลับไปจองคิวใหม่ได้</span>
          </div>
        )}

        <div className="ticket-qr">
          <QrImage value={ticketUrl} label={`QR บัตรคิว ${ticket.queueNumber}`} size={220} />
          <p>QR นี้ใช้เปิดดูสถานะของบัตรคิวนี้</p>
        </div>
        <div className="ticket-meta">
          <span>วันที่</span>
          <strong>{new Intl.DateTimeFormat("th-TH", { dateStyle: "long" }).format(new Date(ticket.queueDate))}</strong>
        </div>
      </section>
      <p className="refresh-note"><i /> สถานะอัปเดตอัตโนมัติทุก 10 วินาที</p>
    </Layout>
  );
}
